import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useGate } from "../../lib/api";
import { SectionCard, Stat } from "./shared";

/** Today at the gate (HRX-02): who is in, who is late, who has not arrived.
 *
 * Built from punches rather than from Attendance, because Attendance is written by a scheduled job after the
 * fact and the question a supervisor asks at ten in the morning is about right now. The people who need looking
 * at come first: nobody scrolls past ninety rows to find the eight who did not turn up. */
/** A day still running is not graded, so it gets its own two states rather than borrowing "Absent". */
const STATUS_PILL: Record<string, string> = {
  Present: "bg-green-50 text-status-green",
  "In progress": "bg-blue-50 text-brand-primary",
  "Half Day": "bg-amber-50 text-status-amber",
  "Not arrived": "bg-brand-grey-light text-brand-grey",
  Absent: "bg-red-50 text-status-red",
};

export default function Gate({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useGate(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const b = d.board;

  return (
    <div className="mt-4 space-y-4" data-hr-gate>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-brand-grey">{t("hr.gate.hint", { date: d.date, shifts: d.shifts.join(", ") })}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6" data-hr-gate-board>
        <Stat label={t("hr.gate.onSite")} value={b.on_site} cls="text-status-green" />
        <Stat label={t("hr.gate.clockedOut")} value={b.clocked_out} />
        <Stat label={t("hr.gate.notArrived")} value={b.not_arrived} cls={b.not_arrived ? "text-status-amber" : ""} />
        <Stat label={t("hr.gate.late")} value={b.late} cls={b.late ? "text-status-amber" : ""} />
        <Stat label={t("hr.gate.overtime")} value={b.overtime} />
        <Stat label={t("hr.gate.hours")} value={`${b.hours_today.toFixed(0)} h`} hint={t("hr.gate.hoursHint")} />
      </div>

      <SectionCard title={t("hr.gate.title")} aside={t("hr.gate.expected", { n: b.expected })}>
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-4 py-2">{t("hr.gate.shift")}</th>
                <th className="px-2 py-2">{t("hr.gate.in")}</th>
                <th className="px-2 py-2">{t("hr.gate.out")}</th>
                <th className="px-2 py-2 text-right">{t("hr.gate.worked")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-hr-gate-row={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">
                      {r.company ? <CompanyLogo company={r.company} size={12} className="mr-1 align-middle" /> : null}
                      {r.designation}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-[11px] text-brand-grey">{r.shift}</td>
                  <td className="px-2 py-2 tabular-nums">
                    {r.first_in ? r.first_in.slice(11, 16) : "–"}
                    {r.late_entry && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] font-semibold text-status-amber">+{r.late_by_minutes}</span>}
                  </td>
                  <td className="px-2 py-2 tabular-nums">
                    {r.last_out ? r.last_out.slice(11, 16) : (r.on_site ? <span className="text-status-green">{t("hr.gate.stillIn")}</span> : "–")}
                    {r.early_exit && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] font-semibold text-status-amber">{t("hr.gate.early")}</span>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {r.hours ? `${r.hours.toFixed(2)} h` : "–"}
                    {r.overtime_hours ? <span className="ml-1 text-[10px] font-semibold text-brand-primary">+{r.overtime_hours}</span> : null}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[r.status] ?? "bg-red-50 text-status-red"}`}>
                      {t(`my.clock.statuses.${r.status}`, r.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
